import ExcelJS from 'exceljs';
import { formatDate, formatDateTime } from '@/helpers/formatting';
import { useTransactionTranslations } from './useTransactionTranslations';
import { useCallback } from 'react';
import {
  OrganizationDTO,
  OrganizationUsersResponseDTO,
  TravellerDTO,
  TravellerMembershipDTO,
  TransactionWithBillingDetailsDTO,
  TransactionWithProductsDTO,
  useGetDepartmentsList,
  useGetTenant,
  useListPayers,
} from '@/api/trafikportalen';
import { useStatusOptions } from './useStatusOptions';
import { formatScheduledInactivation } from '@/helpers/membershipStatus';
import { getTravellerStatus } from '@/helpers/status';
import { useIntl } from 'react-intl';
import { toast } from '@/hooks/useToast';
import { useFileDownload } from './useFileDownload';
import { payerDetailsTranslations } from '@/helpers/payerDetails';
import { getDepartmentPayer } from '@/helpers/departmentPayer';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import { makeUnique } from '../helpers/utils';
import { DateTime } from 'luxon';
import {
  COLLECTION_TYPE_LABELS,
  STUDENT_COLLECTION_TYPES,
} from '@/helpers/collectionTypes';
import type { FormattedStudent, StudentType } from '@/types/students';
import { useHalfProductAvailability } from '@/hooks/useHalfProductAvailability';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import {
  FieldMapping,
  getChildAndYouthFieldMappings,
  getStudentTravelFieldMappings,
} from '@/components/Import/fieldsLegendData';

/**
 * Export transactions to Excel for sysadmins/tenant users.
 * Uses all data from the exportTransactionsHook including organization names.
 */
export const useExportTransactionsToExcelForTenant = () => {
  const { getSourceTranslation, getStateTranslation } =
    useTransactionTranslations();
  const { downloadBlob } = useFileDownload();
  const { data: tenant } = useGetTenant();
  const { formatMessage } = useIntl();

  return useCallback(
    (transactions: TransactionWithBillingDetailsDTO[]) => {
      if (!transactions.length) {
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Transaktioner');

      const requiredPayerDetails = makeUnique([
        ...(tenant?.required_internal_invoicing_details || []),
        ...(tenant?.required_external_invoicing_details || []),
      ]);

      const columns = [
        { header: 'Orderdatum', key: 'date', width: 20 },
        { header: 'Status', key: 'status', width: 15 },
        { header: 'Titel', key: 'title', width: 30 },
        { header: 'Detaljer', key: 'details', width: 50 },
        { header: 'Syfte med resan', key: 'reason', width: 30 },
        { header: 'Antal', key: 'amount', width: 7 },
        { header: 'Källa', key: 'source', width: 20 },
        { header: 'Pris inkl. moms', key: 'priceIncVat', width: 15 },
        { header: 'Pris exkl. moms', key: 'priceExVat', width: 15 },
        { header: 'Moms', key: 'vat', width: 10 },
        { header: 'Organisation', key: 'organization', width: 50 },
        { header: 'Intern organisation', key: 'internal', width: 7 },
        { header: 'Avdelning', key: 'department', width: 35 },
        { header: 'Referens', key: 'payerReference', width: 25 },
      ];

      requiredPayerDetails.forEach((field) => {
        columns.push({
          header:
            payerDetailsTranslations[
              field as keyof typeof payerDetailsTranslations
            ] || field,
          key: `payer_${field}`,
          width: 20,
        });
      });

      columns.push(
        { header: 'Bobcat Order-ID', key: 'bobcatOrderId', width: 25 },
        {
          header: 'Bobcat Transaction-ID',
          key: 'bobcatTransactionId',
          width: 25,
        },
        { header: 'Produkt-ID', key: 'productId', width: 25 },
        { header: 'Transaktions-ID', key: 'transactionId', width: 25 },
        {
          header: 'Återbetalnings-ID',
          key: 'refundedTransactionId',
          width: 25,
        },
      );

      worksheet.columns = columns;

      transactions.forEach(
        ({
          transaction,
          transaction_product: product,
          organization,
          department,
          payer_reference,
          payer_details,
        }) => {
          const row: Record<string, string | number> = {
            date: DateTime.fromISO(transaction.created_at).toFormat(
              'yyyy-MM-dd, HH:mm:ss',
            ),
            status: getStateTranslation({ state: transaction.state }),
            title: product.title,
            details: product.subtitle || '',
            reason: transaction.reason || '',
            amount: product.count,
            source: getSourceTranslation({ source: transaction.source }),
            priceIncVat: transaction.amount_inc_vat,
            priceExVat: transaction.amount_ex_vat,
            vat: transaction.amount_inc_vat - transaction.amount_ex_vat,
            organization: organization.name,
            internal: organization.is_internal ? 'Ja' : 'Nej',
            department: department.name,
            payerReference: payer_reference || '-',
            bobcatOrderId: transaction.bobcat_order_id || '',
            bobcatTransactionId: transaction.bobcat_transaction_id || '',
            productId: product.id,
            transactionId: transaction.id,
            refundedTransactionId: transaction.refunded_transaction_id || '',
          };

          requiredPayerDetails.forEach((field) => {
            row[`payer_${field}`] = payer_details?.[field] || '-';
          });

          worksheet.addRow(row);
        },
      );

      const dateStr = formatDate(new Date().toISOString());
      const filename = `Transaktioner_${dateStr}.xlsx`;

      workbook.xlsx
        .writeBuffer()
        .then((buffer) => {
          const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          downloadBlob(blob, filename);
        })
        .catch((err) => {
          console.error('Excel export failed', err);
          toast.error(
            {
              title: formatMessage({
                defaultMessage: 'Excel-exporten misslyckades',
              }),
            },
            { autoClose: 3000 },
          );
        });
    },
    [
      tenant,
      getSourceTranslation,
      getStateTranslation,
      downloadBlob,
      formatMessage,
    ],
  );
};

/**
 * Export transactions to Excel for organization users.
 * Fetches departments and payers separately since exportTransactionsHook is sysadmin-only.
 * Excludes organization column and includes payer details.
 */
export const useExportTransactionsToExcelForOrg = () => {
  const { getSourceTranslation, getStateTranslation } =
    useTransactionTranslations();
  const { downloadBlob } = useFileDownload();
  const { data: tenant } = useGetTenant();
  const { formatMessage } = useIntl();

  const isSysAdmin = checkUserPermissions({ permission: 'tenant:viewer' });

  const { data: orgPayers } = useListPayers({
    query: { enabled: !isSysAdmin },
  });
  const { data: departments } = useGetDepartmentsList({
    query: { enabled: !isSysAdmin },
  });

  return useCallback(
    (
      transactions: TransactionWithProductsDTO[],
      departmentsMap?: Record<string, string>,
    ) => {
      if (!transactions.length) {
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Transaktioner');

      const requiredPayerDetails = makeUnique([
        ...(tenant?.required_internal_invoicing_details || []),
        ...(tenant?.required_external_invoicing_details || []),
      ]);

      const columns = [
        { header: 'Orderdatum', key: 'date', width: 20 },
        { header: 'Status', key: 'status', width: 15 },
        { header: 'Titel', key: 'title', width: 30 },
        { header: 'Detaljer', key: 'details', width: 50 },
        { header: 'Syfte med resan', key: 'reason', width: 30 },
        { header: 'Antal', key: 'amount', width: 7 },
        { header: 'Källa', key: 'source', width: 20 },
        { header: 'Pris inkl. moms', key: 'priceIncVat', width: 15 },
        { header: 'Pris exkl. moms', key: 'priceExVat', width: 15 },
        { header: 'Moms', key: 'vat', width: 10 },
        { header: 'Avdelning', key: 'department', width: 35 },
        { header: 'Referens', key: 'payerReference', width: 25 },
      ];

      requiredPayerDetails.forEach((field) => {
        columns.push({
          header:
            payerDetailsTranslations[
              field as keyof typeof payerDetailsTranslations
            ] || field,
          key: `payer_${field}`,
          width: 20,
        });
      });

      columns.push(
        { header: 'Bobcat Order-ID', key: 'bobcatOrderId', width: 25 },
        {
          header: 'Bobcat Transaction-ID',
          key: 'bobcatTransactionId',
          width: 25,
        },
        { header: 'Produkt-ID', key: 'productId', width: 25 },
        { header: 'Transaktions-ID', key: 'transactionId', width: 25 },
        {
          header: 'Återbetalnings-ID',
          key: 'refundedTransactionId',
          width: 25,
        },
      );

      worksheet.columns = columns;

      transactions.forEach(({ transaction, transaction_product: product }) => {
        const payer = getDepartmentPayer(
          transaction.department_id,
          departments || [],
          orgPayers,
        );

        const row: Record<string, string | number> = {
          date: DateTime.fromISO(transaction.created_at).toFormat(
            'yyyy-MM-dd, HH:mm:ss',
          ),
          status: getStateTranslation({ state: transaction.state }),
          title: product.title,
          details: product.subtitle || '',
          reason: transaction.reason || '',
          amount: product.count,
          source: getSourceTranslation({ source: transaction.source }),
          priceIncVat: transaction.amount_inc_vat,
          priceExVat: transaction.amount_ex_vat,
          vat: transaction.amount_inc_vat - transaction.amount_ex_vat,
          department: departmentsMap?.[transaction.department_id] || '',
          payerReference: payer?.reference || '-',
          bobcatOrderId: transaction.bobcat_order_id || '',
          bobcatTransactionId: transaction.bobcat_transaction_id || '',
          productId: product.id,
          transactionId: transaction.id,
          refundedTransactionId: transaction.refunded_transaction_id || '',
        };

        requiredPayerDetails.forEach((field) => {
          row[`payer_${field}`] = payer?.details?.[field] || '-';
        });

        worksheet.addRow(row);
      });

      const dateStr = formatDate(new Date().toISOString());
      const filename = `Transaktioner_${dateStr}.xlsx`;

      workbook.xlsx
        .writeBuffer()
        .then((buffer) => {
          const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          downloadBlob(blob, filename);
        })
        .catch((err) => {
          console.error('Excel export failed', err);
          toast.error(
            {
              title: formatMessage({
                defaultMessage: 'Excel-exporten misslyckades',
              }),
            },
            { autoClose: 3000 },
          );
        });
    },
    [
      tenant,
      departments,
      orgPayers,
      getSourceTranslation,
      getStateTranslation,
      downloadBlob,
      formatMessage,
    ],
  );
};

export const useExportOrganizationsToExcel = () => {
  const getStatus = useStatusOptions();
  const { downloadBlob } = useFileDownload();
  const { formatMessage } = useIntl();
  const { disableHalfProduct, disableDiscountCodes } = useTenantConfiguration();

  return useCallback(
    (
      organizations: OrganizationDTO[],
      organizationUsers?: OrganizationUsersResponseDTO,
    ) => {
      if (!organizations.length) {
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Organisationer');

      const yes = formatMessage({ defaultMessage: 'Ja' });
      const no = formatMessage({ defaultMessage: 'Nej' });

      const columns: { header: string; key: string; width: number }[] = [
        { header: 'Namn', key: 'namn', width: 30 },
        {
          header: 'Organisationsnummer',
          key: 'organisationsnummer',
          width: 20,
        },
        { header: 'Status', key: 'status', width: 15 },
        { header: 'Kredit', key: 'kredit', width: 10 },
        { header: 'Administratörer', key: 'administratorer', width: 40 },
        { header: 'E-postadresser', key: 'epostadresser', width: 45 },
        ...(disableDiscountCodes
          ? []
          : [{ header: 'Rabattkod', key: 'rabattkod', width: 15 }]),
        { header: 'Intern', key: 'intern', width: 10 },
      ];

      for (const ct of STUDENT_COLLECTION_TYPES) {
        const label = formatMessage(COLLECTION_TYPE_LABELS[ct]);
        columns.push({
          header: `${label}: aktiv`,
          key: `${ct}_active`,
          width: 15,
        });
        if (disableDiscountCodes) continue;
        if (!disableHalfProduct) {
          columns.push({
            header: `${label}: rabattkod halv`,
            key: `${ct}_discount_half`,
            width: 20,
          });
        }
        columns.push({
          header: `${label}: rabattkod`,
          key: `${ct}_discount_full`,
          width: 20,
        });
      }

      columns.push({ header: 'Skapad', key: 'skapad', width: 20 });

      worksheet.columns = columns;

      organizations.forEach((org) => {
        const orgUsers = (organizationUsers?.[org.id] || []).filter(
          (user) => !user.user_profile.expires_at,
        );
        const adminNames = orgUsers
          .map((user) => `${user.firstname} ${user.lastname}`)
          .join('\n');
        const adminEmails = orgUsers.map((user) => user.email).join('\n');

        const row: Record<string, string | number> = {
          namn: org.name,
          organisationsnummer: org.organization_number,
          status: getStatus({ status: org.state })?.label ?? org.state,
          kredit: org.credit ?? 0,
          administratorer: adminNames || '',
          epostadresser: adminEmails || '',
          intern: org.is_internal ? yes : no,
          skapad: formatDateTime(org.created_at),
        };
        if (!disableDiscountCodes) {
          row.rabattkod = org.discount_codes?.master?.full_product ?? '';
        }

        for (const ct of STUDENT_COLLECTION_TYPES) {
          const isActive = org.enabled_collection_types?.includes(ct);
          const codes = org.discount_codes?.[ct];
          row[`${ct}_active`] = isActive ? yes : no;
          if (disableDiscountCodes) continue;
          if (!disableHalfProduct) {
            row[`${ct}_discount_half`] = codes?.half_product ?? '';
          }
          row[`${ct}_discount_full`] = codes?.full_product ?? '';
        }

        const excelRow = worksheet.addRow(row);

        const adminCol =
          worksheet.columns?.findIndex((c) => c.key === 'administratorer') ?? 4;
        const emailCol =
          worksheet.columns?.findIndex((c) => c.key === 'epostadresser') ?? 5;
        const adminCell = excelRow.getCell(adminCol + 1);
        adminCell.alignment = { wrapText: true, vertical: 'top' };

        const emailCell = excelRow.getCell(emailCol + 1);
        emailCell.alignment = { wrapText: true, vertical: 'top' };
      });

      const dateStr = formatDate(new Date().toISOString());
      const filename = `Organisationer_${dateStr}.xlsx`;

      // Write to buffer and trigger download
      workbook.xlsx
        .writeBuffer()
        .then((buffer) => {
          const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          downloadBlob(blob, filename);
        })
        .catch((err) => {
          console.error('Excel export failed', err);
          toast.error(
            {
              title: formatMessage({
                defaultMessage: 'Excel-exporten misslyckades',
              }),
            },
            { autoClose: 3000 },
          );
        });
    },
    [
      getStatus,
      downloadBlob,
      formatMessage,
      disableHalfProduct,
      disableDiscountCodes,
    ],
  );
};

export const useExportStudentsToExcel = () => {
  const { formatMessage } = useIntl();
  const { downloadBlob } = useFileDownload();
  const isHalfProductAvailable = useHalfProductAvailability();

  return useCallback(
    (students: FormattedStudent[], studentType: StudentType) => {
      if (!students.length) {
        return;
      }

      const disableHalfProduct = !isHalfProductAvailable(studentType);

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Elever');

      const childAndYouthExcludedColumnKeys = new Set([
        'guardian1_address_care_of',
        'guardian1_address_street',
        'guardian1_address_zip',
        'guardian1_address_city',
        'guardian1_bus_stop',
        'guardian2_address_care_of',
        'guardian2_address_street',
        'guardian2_address_zip',
        'guardian2_address_city',
        'guardian2_bus_stop',
        'school_name',
        'school_address_street',
        'school_address_zip',
        'school_address_city',
        'school_bus_stop',
      ]);

      const allColumns = [
        { header: 'Förnamn', key: 'first_name', width: 20 },
        { header: 'Efternamn', key: 'last_name', width: 20 },
        { header: 'Personnummer', key: 'national_identity_number', width: 20 },
        { header: 'E-post', key: 'email', width: 30 },
        {
          header: 'Vårdnadshavare 1: E-post',
          key: 'guardian1_email',
          width: 30,
        },
        {
          header: 'Vårdnadshavare 1: c/o',
          key: 'guardian1_address_care_of',
          width: 25,
        },
        {
          header: 'Vårdnadshavare 1: Adress',
          key: 'guardian1_address_street',
          width: 25,
        },
        {
          header: 'Vårdnadshavare 1: Postnummer',
          key: 'guardian1_address_zip',
          width: 20,
        },
        {
          header: 'Vårdnadshavare 1: Postort',
          key: 'guardian1_address_city',
          width: 20,
        },
        {
          header: 'Vårdnadshavare 1: Hållplats',
          key: 'guardian1_bus_stop',
          width: 25,
        },
        {
          header: 'Vårdnadshavare 2: E-post',
          key: 'guardian2_email',
          width: 30,
        },
        {
          header: 'Vårdnadshavare 2: c/o',
          key: 'guardian2_address_care_of',
          width: 25,
        },
        {
          header: 'Vårdnadshavare 2: Adress',
          key: 'guardian2_address_street',
          width: 25,
        },
        {
          header: 'Vårdnadshavare 2: Postnummer',
          key: 'guardian2_address_zip',
          width: 20,
        },
        {
          header: 'Vårdnadshavare 2: Postort',
          key: 'guardian2_address_city',
          width: 20,
        },
        {
          header: 'Vårdnadshavare 2: Hållplats',
          key: 'guardian2_bus_stop',
          width: 25,
        },
        { header: 'Skola', key: 'school_name', width: 25 },
        { header: 'Skolans adress', key: 'school_address_street', width: 25 },
        {
          header: 'Skolans postnummer',
          key: 'school_address_zip',
          width: 18,
        },
        { header: 'Skolans postort', key: 'school_address_city', width: 20 },
        { header: 'Skolans hållplats', key: 'school_bus_stop', width: 20 },
        {
          header: 'Leverans: c/o',
          key: 'delivery_address_care_of',
          width: 25,
        },
        {
          header: 'Leverans: Adress',
          key: 'delivery_address_street',
          width: 25,
        },
        {
          header: 'Leverans: Postnummer',
          key: 'delivery_address_zip',
          width: 18,
        },
        {
          header: 'Leverans: Postort',
          key: 'delivery_address_city',
          width: 20,
        },
        {
          header: 'E-post i appen / Mina sidor',
          key: 'bobcat_private_email',
          width: 36,
        },
        { header: 'Halv produkt', key: 'half_product', width: 15 },
        { header: 'Aktiv', key: 'active', width: 10 },
        { header: 'Manuellt ändrad', key: 'manually_modified', width: 15 },
        { header: 'Skapad', key: 'created_at', width: 20 },
        { header: 'Uppdaterad', key: 'updated_at', width: 20 },
      ];

      let resolvedColumns =
        studentType === 'child-and-youth'
          ? allColumns.filter(
              (col) => !childAndYouthExcludedColumnKeys.has(col.key),
            )
          : allColumns;

      if (disableHalfProduct) {
        resolvedColumns = resolvedColumns.filter(
          (col) => col.key !== 'half_product',
        );
      }

      worksheet.columns = resolvedColumns;

      const fieldMappings =
        studentType === 'child-and-youth'
          ? getChildAndYouthFieldMappings(disableHalfProduct)
          : getStudentTravelFieldMappings(disableHalfProduct);

      const mappingByKey = new Map<string, FieldMapping>(
        fieldMappings.map((f) => [
          f.apiField.replace(/\./g, '_').replace(/_zip_code$/, '_zip'),
          f,
        ]),
      );

      const extraInfoMessages: Record<string, string> = {
        'import.guardianAddressInfo.requiredForGuardian1': formatMessage({
          id: 'import.guardianAddressInfo.requiredForGuardian1',
        }),
        'import.guardianAddressInfo.usedForTravelSearch': formatMessage({
          id: 'import.guardianAddressInfo.usedForTravelSearch',
        }),
        'import.guardianAddressInfo.busStopOptional': formatMessage({
          id: 'import.guardianAddressInfo.busStopOptional',
        }),
        'import.deliveryAddressInfo.cardDelivery': formatMessage({
          id: 'import.deliveryAddressInfo.cardDelivery',
        }),
      };

      const headerRow = worksheet.getRow(1);
      resolvedColumns.forEach((col, index) => {
        const mapping = col.key ? mappingByKey.get(col.key) : undefined;
        if (!mapping) {
          return;
        }
        const cell = headerRow.getCell(index + 1);
        if (!mapping.required) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD9D9D9' },
          };
        }
        const requiredLabel = mapping.required
          ? formatMessage({ defaultMessage: 'Obligatorisk' })
          : formatMessage({ defaultMessage: 'Valfri' });
        const noteParts = [
          requiredLabel,
          mapping.description,
          ...(mapping.extraInfo ?? []).map((id) => extraInfoMessages[id] ?? id),
        ].filter(Boolean);
        cell.note = noteParts.join('\n\n');
      });

      const yes = formatMessage({ defaultMessage: 'Ja' });
      const no = formatMessage({ defaultMessage: 'Nej' });

      students.forEach((student) => {
        const rowData: Record<string, string> = {
          first_name: student.first_name || '',
          last_name: student.last_name || '',
          national_identity_number: student.national_identity_number || '',
          email: student.email || '',
          phone: student.phone || '',
          bobcat_private_email: student.bobcat_private_email || '',
          delivery_address_care_of: student.delivery_address?.care_of || '',
          delivery_address_street: student.delivery_address?.street || '',
          delivery_address_zip: student.delivery_address?.zip_code || '',
          delivery_address_city: student.delivery_address?.city || '',
          school_name: student.school_name || '',
          school_address_street: student.school_address?.street || '',
          school_address_zip: student.school_address?.zip_code || '',
          school_address_city: student.school_address?.city || '',
          school_bus_stop: student.school_bus_stop || '',
          guardian1_email: student.guardian1_email || '',
          guardian1_address_care_of: student.guardian1_address?.care_of || '',
          guardian1_address_street: student.guardian1_address?.street || '',
          guardian1_address_zip: student.guardian1_address?.zip_code || '',
          guardian1_address_city: student.guardian1_address?.city || '',
          guardian1_bus_stop: student.guardian1_bus_stop || '',
          guardian2_email: student.guardian2_email || '',
          guardian2_address_care_of: student.guardian2_address?.care_of || '',
          guardian2_address_street: student.guardian2_address?.street || '',
          guardian2_address_zip: student.guardian2_address?.zip_code || '',
          guardian2_address_city: student.guardian2_address?.city || '',
          guardian2_bus_stop: student.guardian2_bus_stop || '',
          active: student.active ? yes : no,
          manually_modified: student.manually_modified ? yes : no,
          created_at: formatDateTime(student.created_at),
          updated_at: formatDateTime(student.updated_at),
        };

        if (!disableHalfProduct) {
          rowData.half_product = student.half_product ? yes : no;
        }

        worksheet.addRow(rowData);
      });

      const studentTypeLabels: Record<StudentType, string> = {
        travels: formatMessage({ id: 'tab.travels' }),
        transportation: formatMessage({ id: 'tab.transportation' }),
        'child-and-youth': formatMessage({ id: 'tab.child-and-youth' }),
      };
      const label = studentTypeLabels[studentType];
      const dateStr = formatDate(new Date().toISOString());
      const filename = `${label}_${dateStr}.xlsx`;

      workbook.xlsx
        .writeBuffer()
        .then((buffer) => {
          const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          downloadBlob(blob, filename);
        })
        .catch((err) => {
          console.error('Excel export failed', err);
          toast.error(
            {
              title: formatMessage({
                defaultMessage: 'Excel-exporten misslyckades',
              }),
            },
            { autoClose: 3000 },
          );
        });
    },
    [formatMessage, downloadBlob, isHalfProductAvailable],
  );
};

export interface ExportTraveller extends TravellerDTO {
  memberships: TravellerMembershipDTO[];
  allpayCredit: number;
  cardCredit: number;
}

export const useExportTravellersToExcel = () => {
  const { formatMessage } = useIntl();
  const { downloadBlob } = useFileDownload();

  return useCallback(
    (travellers: ExportTraveller[]) => {
      if (!travellers.length) {
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Resenärer');

      worksheet.columns = [
        { header: 'Förnamn', key: 'fornamn', width: 20 },
        { header: 'Efternamn', key: 'efternamn', width: 20 },
        { header: 'E-post', key: 'epost', width: 35 },
        { header: 'Telefonnummer', key: 'telefonnummer', width: 15 },
        { header: 'Avdelningar', key: 'avdelningar', width: 40 },
        { header: 'Status', key: 'status', width: 20 },
        { header: 'Tjänsteresa på kort', key: 'kortKredit', width: 20 },
        { header: 'Tjänsteresa i appen', key: 'allpayKredit', width: 20 },
        { header: 'Skapad', key: 'skapad', width: 20 },
      ];

      travellers.forEach((traveller) => {
        const departments = traveller.memberships
          .map((m) => m.department_name)
          .filter(Boolean)
          .join('\n');

        const statusKey = getTravellerStatus({
          traveller,
          memberships: traveller.memberships,
        });

        let statusLabel = '';
        if (statusKey === 'active') {
          statusLabel = formatMessage({ defaultMessage: 'Aktiv' });
        } else if (
          statusKey === 'will-inactivate' ||
          statusKey === 'pending-inactivation'
        ) {
          const inactivateAt = traveller.memberships.find(
            (membership) =>
              membership.inactivate_at &&
              DateTime.fromISO(membership.inactivate_at) > DateTime.now(),
          )?.inactivate_at;
          statusLabel = formatScheduledInactivation(
            formatMessage,
            inactivateAt,
          );
        } else if (statusKey === 'inactive') {
          statusLabel = formatMessage({ defaultMessage: 'Inaktiv' });
        }

        const row = worksheet.addRow({
          fornamn: traveller.first_name || '',
          efternamn: traveller.last_name || '',
          epost: traveller.email || '',
          telefonnummer: traveller.phone || '',
          avdelningar: departments,
          status: statusLabel,
          kortKredit: traveller.cardCredit,
          allpayKredit: traveller.allpayCredit,
          skapad: formatDateTime(traveller.created_at),
        });

        const departmentsCell = row.getCell(5); // Avdelningar column
        departmentsCell.alignment = { wrapText: true, vertical: 'top' };
      });

      const dateStr = formatDate(new Date().toISOString());
      const filename = `Resenarer_${dateStr}.xlsx`;

      // Write to buffer and trigger download
      workbook.xlsx
        .writeBuffer()
        .then((buffer) => {
          const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          downloadBlob(blob, filename);
        })
        .catch((err) => {
          console.error('Excel export failed', err);
          toast.error(
            {
              title: formatMessage({
                defaultMessage: 'Excel-exporten misslyckades',
              }),
            },
            { autoClose: 3000 },
          );
        });
    },
    [formatMessage, downloadBlob],
  );
};
